/**
 * Shared client logout steps (REST logout, credentials, socket, stores).
 * Used by API interceptors, WebSocket fatal-auth handlers, and LogoutButton.
 */
import { gamificationCache } from '@/domains/gamification/utils/gamificationCache';
import {
  getRefreshToken,
  clearCredentials,
  clearCredentialsFromCookies,
} from '@/shared/utils/tokenHelper';
import { disconnectSocket } from '@/shared/services/webSocket';
import { cleanup as cleanupTokenRefresh } from '@/shared/services/tokenRefreshService';
import router from '@/router';
import { isGuestSession } from '@/shared/utils/authSession.util';
import { liveSocketKeepAlive } from '@/domains/live/services/liveSocketKeepAlive';
import { getUserStoreSafe } from '@/shared/utils/piniaSafe.util';

const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';

async function callLogoutApi() {
  const { apiClient } = await import('@/shared/services/api');
  if (isUseCookies) {
    await apiClient.post('/auth/cookie/logout').catch(() => {});
    return;
  }
  const refreshTokenForLogout = getRefreshToken();
  await apiClient.post('/auth/logout', { refreshToken: refreshTokenForLogout ?? '' }).catch(() => {});
}

/** Shared realtime + store teardown after credentials are cleared. */
export async function teardownRealtimeClientState() {
  try {
    getUserStoreSafe()?.reset();
  } catch {
    // Pinia may not be ready in edge teardown paths
  }

  try {
    const { notificationStore } = await import('@/domains/notifications/store/notificationStore');
    const notifications = notificationStore();
    if (typeof notifications.clearNotifications === 'function') {
      notifications.clearNotifications();
    }
  } catch {
    // Pinia may not be ready in edge teardown paths
  }

  try {
    const { useCallStore } = await import('@/domains/chat/store/callStore');
    useCallStore().resetForSessionLogout();
  } catch {
    // Pinia may not be ready in edge teardown paths
  }

  try {
    const { useLiveStore } = await import('@/domains/live/store/liveStore');
    useLiveStore().reset();
  } catch {
    // Pinia may not be ready in edge teardown paths
  }

  try {
    const { resetFriendsSessionState } = await import('@/domains/friends/composables/useFriends');
    resetFriendsSessionState();
  } catch {
    // Pinia may not be ready in edge teardown paths
  }

  try {
    const { resetChatWebSocketSessionState } = await import('@/domains/chat/composables/useChatWebSocket');
    resetChatWebSocketSessionState();
  } catch {
    // Pinia may not be ready in edge teardown paths
  }

  try {
    const { useChatStore } = await import('@/domains/chat/store/chatStore');
    useChatStore().reset();
  } catch {
    // Pinia may not be ready in edge teardown paths
  }

  try {
    const { chatModalStore } = await import('@/domains/chat/store/chatModalStore');
    chatModalStore().clearChatModals();
  } catch {
    // Pinia may not be ready in edge teardown paths
  }

  try {
    const { default: notificationService } = await import(
      '@/domains/notifications/services/NotificationService'
    );
    notificationService.cleanup();
  } catch {
    // Pinia / notification module may be unavailable during edge teardown
  }

  liveSocketKeepAlive.clear();
  disconnectSocket();
}

/**
 * @param {{ securityMessage?: string|null, redirectToSignIn?: boolean, skipLogoutApi?: boolean }} [options]
 */
export async function performSessionLogout(options = {}) {
  const { securityMessage = null, redirectToSignIn = true, skipLogoutApi = false } = options;

  if (isGuestSession()) return;

  const { getAuthStoreSafe } = await import('@/shared/utils/piniaSafe.util');
  const authStore = getAuthStoreSafe();
  if (!authStore || authStore.isLoggedOut) return;

  cleanupTokenRefresh();

  if (securityMessage) {
    authStore.setLogoutSecurityMessage(securityMessage);
  }

  // Logout API must run before markAsLoggedOut — interceptors block non-auth endpoints when logged out.
  if (!skipLogoutApi) {
    await callLogoutApi();
  }

  authStore.markAsLoggedOut();
  gamificationCache.clearAll();

  clearCredentials();
  clearCredentialsFromCookies();

  await teardownRealtimeClientState();

  if (redirectToSignIn) {
    await router.push({ name: 'auth1.sign-in1' });
  }
}
